import React, { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  InputAdornment,
  Typography,
  Menu,
  IconButton,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Formik } from 'formik';
import { Observer } from 'mobx-react';
import { toJS } from 'mobx';
import { useHistory } from 'react-router-dom';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { ListboxComponent } from '../util/ReactSelectOverrides';

import { useRootStore } from '../../stores/BridgeProvider';
import Spinner from '../util/Spinner';
import {
  StyledForm,
  ButtonFlex,
  ErrorText,
  FormRow,
  RowItem,
} from '../styles/styles';
import {
  safeFalsy,
  safeEval,
  formInitialPriceHandler,
} from '../util/UtilFunctions';
import { DealTransactionSchema } from '../../validation';
import ACDropdown from '../util/ACDropdown';
import AccountSelectAC from '../util/AccountSelectAC';

const DealTransactionForm = ({
  onSubmit,
  dealTran = {},
  dealId,
  menuItems,
  buttons,
  submitLabel = 'Submit',
}) => {
  const { dataStore, authStore, orgStore } = useRootStore();
  const history = useHistory();
  const [isLoading, setLoading] = useState(true);
  const [isCreating, setCreating] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const contacts = dataStore.contactArray;
  const transactionCategories = dataStore.validTransactionCategoryArray;
  const accounts = dataStore.validAccountArray;
  const open = Boolean(anchorEl);
  const { settings } = orgStore;
  const {
    VoucherTypeDefault,
    ARAccountId: defaultAR,
    APAccountId: defaultAP,
    ARBankAccountId: defaultARBank,
    APBankAccountId: defaultAPBank,
  } = settings;

  const handleClick = e => {
    e.stopPropagation;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const deleteDealTransaction = async () => {
    try {
      await authStore.request.delete(
        `${process.env.REACT_APP_API}/api/deal-transaction`,
        {
          data: { DealTransactionId: dealTran.DealTranId },
        },
      );
      history.push(`/deal/${dealTran.DealId}`);
    } catch (e) {
      console.error(e);
    }
  };

  const filter = createFilterOptions({
    limit: 80,
  });

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      if (accounts.length === 0) {
        requests.push(dataStore.retrieveAccounts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  if (isLoading) return <Spinner />;
  return (
    <Observer
      render={() => {
        return (
          <Formik
            validationSchema={DealTransactionSchema}
            initialValues={{
              // TODO: Change (dealTransactionId, category) on server to match
              DealTransactionId: dealTran.DealTranId,
              DealId: dealTran.DealId || dealId,
              Type: safeFalsy(dealTran.TranType),
              CategoryId: safeFalsy(dealTran.CategoryId),
              Contact: dataStore.getContactFromId(dealTran.ContactId),
              ContactId: safeFalsy(dealTran.ContactId),
              Price: formInitialPriceHandler(dealTran.Price),
              Quantity: dealTran.Quantity
                ? safeEval(() => parseInt(dealTran.Quantity, 10), 1)
                : 1,
              Description: safeFalsy(dealTran.Description),
              DebitAccount: safeFalsy(dealTran.DebitAccount, null),
              DebitAccountObject: dataStore.getAccountFromId(
                dealTran.DebitAccount,
              ),
              CreditAccount: safeFalsy(dealTran.CreditAccount, null),
              CreditAccountObject: dataStore.getAccountFromId(
                dealTran.CreditAccount,
              ),
            }}
            onSubmit={async (values, actions) => {
              try {
                await onSubmit(values);
              } catch (e) {
                console.error(e);
              }
              actions.setSubmitting(false);
            }}
            render={({
              handleBlur,
              handleChange,
              errors,
              handleSubmit,
              values,
              isSubmitting,
              setFieldValue,
              dirty,
              touched,
            }) => {
              const showError = val => errors[val] && touched[val];
              const handleCreateContact = async inputValue => {
                setCreating(true);
                try {
                  const { data: contactData } = await authStore.request.post(
                    `${process.env.REACT_APP_API}/api/contact`,
                    {
                      Name: inputValue,
                      IsAgent: 0,
                    },
                  );
                  const { id: cId } = contactData;
                  await dataStore.retrieveContacts();
                  const contact = dataStore.getContactFromId(cId);
                  setFieldValue('Contact', contact);
                  setFieldValue('ContactId', cId);
                } catch (e) {
                  console.error(e);
                }
                setCreating(false);
              };

              const populateAccounts = (
                type = values.Type,
                category = values.CategoryId,
              ) => {
                if (type && category) {
                  try {
                    const {
                      APAccountId,
                      APContraAccountId,
                      ARAccountId,
                      ARContraAccountId,
                    } = dataStore.getTranCategoryFromId(category);
                    if (type === 'AP') {
                      // If AP --> Ap Account is Credit and AP Contra is debit
                      if (VoucherTypeDefault === 'DP') {
                        const APAccount = dataStore.getAccountFromId(
                          defaultAPBank,
                        );
                        setFieldValue('CreditAccountObject', APAccount);
                        setFieldValue(
                          'CreditAccount',
                          safeEval(() => APAccount.AccountId),
                          null,
                        );
                      } else {
                        const APAccount = dataStore.getAccountFromId(defaultAP);
                        setFieldValue('CreditAccountObject', APAccount);
                        setFieldValue(
                          'CreditAccount',
                          safeEval(() => APAccount.AccountId),
                          null,
                        );
                      }

                      const APContraAccount = dataStore.getAccountFromId(
                        APContraAccountId,
                      );
                      setFieldValue('DebitAccountObject', APContraAccount);
                      setFieldValue(
                        'DebitAccount',
                        safeEval(() => APContraAccount.AccountId),
                        null,
                      );
                    }
                    if (type === 'AR') {
                      // IF AR --> AR account debit & ar contra is credit
                      if (VoucherTypeDefault === 'DP') {
                        const ARAccount = dataStore.getAccountFromId(
                          defaultARBank,
                        );
                        setFieldValue('DebitAccountObject', ARAccount);
                        setFieldValue(
                          'DebitAccount',
                          safeEval(() => ARAccount.AccountId),
                          null,
                        );
                      } else {
                        const ARAccount = dataStore.getAccountFromId(defaultAR);
                        setFieldValue('DebitAccountObject', ARAccount);
                        setFieldValue(
                          'DebitAccount',
                          safeEval(() => ARAccount.AccountId),
                          null,
                        );
                      }

                      const ARContraAccount = dataStore.getAccountFromId(
                        ARContraAccountId,
                      );
                      setFieldValue('CreditAccountObject', ARContraAccount);
                      setFieldValue(
                        'CreditAccount',
                        safeEval(() => ARContraAccount.AccountId),
                        null,
                      );
                    }
                  } catch (e) {
                    console.error(e);
                  }
                }
              };
              const handleChangeTranCategory = value => {
                setFieldValue('CategoryId', value);
                populateAccounts(values.Type, value);
              };

              const handleChangeType = value => {
                setFieldValue('CategoryId', '', false);
                setFieldValue('Type', value, false);
                populateAccounts(value, values.CategoryId);
              };

              const isValidCreditAccountGroup = TypeId => {
                const { Type: TranType } = values;
                if (TranType === 'AP') {
                  return [2].includes(TypeId);
                }
                if (TranType === 'AR') {
                  return [7, 3, 4, 5, 6].includes(TypeId);
                }
                return true;
              };

              const isValidDebitAccountGroup = TypeId => {
                const { Type: TranType } = values;
                if (TranType === 'AR') {
                  return [1].includes(TypeId);
                }
                if (TranType === 'AP') {
                  return [6, 3, 4, 5, 7].includes(TypeId);
                }
                return true;
              };

              const filteredTranCategories = () =>
                transactionCategories.filter(
                  ({
                    APAccountId,
                    APContraAccountId,
                    ARAccountId,
                    ARContraAccountId,
                    TranCategoryId,
                  }) => {
                    const { Type, CategoryId } = values;
                    // If the category is already set then it should exist in the dropdown
                    if (CategoryId && CategoryId === TranCategoryId) {
                      return true;
                    }
                    if (!Type) return true;
                    if (Type === 'AP') {
                      return APAccountId && APContraAccountId;
                    }
                    if (Type === 'AR') {
                      return ARAccountId && ARContraAccountId;
                    }
                    if (Type === 'JE') return true;
                    return false;
                  },
                );

              const moveAccountTypeToFront = (list, typeId) => {
                const sorter = (a, b, arr) => {
                  if (arr.includes(a.TypeId)) {
                    return -1;
                  }
                  if (arr.includes(b.TypeId)) {
                    return 1;
                  }
                  return a.TypeId - b.TypeId;
                };

                return list.sort((a, b) => sorter(a, b, typeId));
              };

              return (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="h2" style={{ margin: '0 auto' }}>
                      {dealTran.DealTranId
                        ? 'Edit Deal Transaction'
                        : 'New Deal Transaction'}
                    </Typography>
                    {menuItems && (
                      <div>
                        <IconButton size="small" onClick={handleClick}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={open}
                          onClose={handleClose}
                        >
                          {menuItems.map(item => item)}
                        </Menu>
                      </div>
                    )}
                  </div>
                  <StyledForm onSubmit={handleSubmit}>
                    <FormRow>
                      <RowItem>
                        <ACDropdown
                          label="Type"
                          textFieldProps={{ margin: 'normal' }}
                          value={values.Type}
                          onChange={(event, newValue) => {
                            handleChangeType(newValue);
                          }}
                          options={['AP', 'AR', 'JE']}
                          getOptionSelected={(option, value) => {
                            if (value === '') {
                              return true;
                            }
                            if (option === value) {
                              return true;
                            }
                          }}
                        />
                      </RowItem>
                      <RowItem>
                        <ACDropdown
                          label={
                            <div>
                              Transaction Category{' '}
                              <span style={{ color: 'red' }}>*</span>
                            </div>
                          }
                          textFieldProps={{ margin: 'normal' }}
                          options={filteredTranCategories()}
                          onChange={(e, newVal) => {
                            newVal !== null &&
                              handleChangeTranCategory(newVal.TranCategoryId);
                          }}
                          value={
                            filteredTranCategories().find(
                              x => x.TranCategoryId === values.CategoryId,
                            ) || null
                          }
                          getOptionLabel={value => value.Code || null}
                          getOptionSelected={(option, value) => {
                            if (value === '') {
                              return true;
                            }
                            if (
                              option.TranCategoryId === value.TranCategoryId
                            ) {
                              return true;
                            }
                          }}
                        />
                      </RowItem>
                    </FormRow>
                    {showError('Type') && <ErrorText>{errors.Type}</ErrorText>}
                    {showError('CategoryId') && (
                      <ErrorText style={{ textAlign: 'right' }}>
                        {errors.CategoryId}
                      </ErrorText>
                    )}

                    <div style={{ width: '100%', zIndex: 1001 }}>
                      <ACDropdown
                        label={
                          <div>
                            Contact <span style={{ color: 'red' }}>*</span>
                          </div>
                        }
                        textFieldProps={{ margin: 'normal' }}
                        value={
                          contacts.find(
                            c => c.ContactId === values.ContactId,
                          ) || null
                        }
                        handleHomeEndKeys
                        ListboxComponent={ListboxComponent}
                        options={contacts}
                        getOptionLabel={({ Name }) => Name || null}
                        getOptionSelected={(option, val) =>
                          option.ContactId === val.ContactId
                        }
                        renderOption={({ isNew, Name }) =>
                          isNew ? `Add "${Name}"` : Name
                        }
                        onChange={async (event, newValue) => {
                          if (newValue !== null && newValue.ContactId) {
                            setFieldValue('ContactId', newValue.ContactId);
                          } else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            const newId = await handleCreateContact(
                              newValue.inputValue,
                            );
                            setFieldValue('ContactId', newId);
                          }
                        }}
                        filterOptions={(options, params) => {
                          const filtered = filter(options, params);

                          const inputValueIsNotAContact =
                            options.findIndex(
                              option => option.Name === params.inputValue,
                            ) < 0;

                          // Suggest the creation of a new value
                          if (
                            params.inputValue !== '' &&
                            inputValueIsNotAContact
                          ) {
                            filtered.push({
                              inputValue: params.inputValue,
                              Name: params.inputValue,
                              isNew: true,
                            });
                          }

                          return filtered;
                        }}
                      />
                    </div>
                    {showError('ContactId') && (
                      <ErrorText>{errors.ContactId}</ErrorText>
                    )}
                    <FormRow>
                      <RowItem>
                        <TextField
                          name="Price"
                          label={
                            <div>
                              Price <span style={{ color: 'red' }}>*</span>
                            </div>
                          }
                          type="number"
                          value={values.Price}
                          margin="normal"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                        {showError('Price') && (
                          <ErrorText>{errors.Price}</ErrorText>
                        )}
                      </RowItem>
                      <RowItem>
                        <TextField
                          name="Quantity"
                          label={
                            <div>
                              Quantity <span style={{ color: 'red' }}>*</span>
                            </div>
                          }
                          value={values.Quantity}
                          margin="normal"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          variant="outlined"
                          type="number"
                          min="0"
                          step="1"
                          pattern="\d*"
                          fullWidth
                        />
                        {showError('Quantity') && (
                          <ErrorText>{errors.Quantity}</ErrorText>
                        )}
                      </RowItem>
                    </FormRow>
                    <FormRow>
                      <RowItem>
                        <div style={{ width: '100%', zIndex: 1000 }}>
                          <AccountSelectAC
                            label={
                              <div>
                                Credit Account{' '}
                                <span style={{ color: 'red' }}>*</span>
                              </div>
                            }
                            value={values.CreditAccountObject}
                            isValidAccountGroup={isValidCreditAccountGroup}
                            prefillDisabled={!!dealTran.DealTranId}
                            changeMemoVals={values.Type}
                            style={{ width: 'inherit', margin: '16px 0 8px' }}
                            sortFunction={list =>
                              values.Type === 'AR'
                                ? moveAccountTypeToFront(list, [6])
                                : list
                            }
                            onChange={(e, val) => {
                              setFieldValue('CreditAccountObject', val);
                              setFieldValue(
                                'CreditAccount',
                                safeEval(() => val.AccountId),
                                null,
                              );
                            }}
                          />
                          {showError('CreditAccount') && (
                            <ErrorText>{errors.CreditAccount}</ErrorText>
                          )}
                        </div>
                      </RowItem>
                      <RowItem>
                        <div style={{ width: '100%', zIndex: 1000 }}>
                          <AccountSelectAC
                            label={
                              <div>
                                Debit Account{' '}
                                <span style={{ color: 'red' }}>*</span>
                              </div>
                            }
                            value={values.DebitAccountObject}
                            isValidAccountGroup={isValidDebitAccountGroup}
                            prefillDisabled={!!dealTran.DealTranId}
                            changeMemoVals={values.Type}
                            style={{ width: 'inherit', margin: '16px 0 8px' }}
                            sortFunction={list =>
                              values.Type === 'AP'
                                ? moveAccountTypeToFront(list, [7])
                                : list
                            }
                            onChange={(e, val) => {
                              setFieldValue('DebitAccountObject', val);
                              setFieldValue(
                                'DebitAccount',
                                safeEval(() => val.AccountId),
                                null,
                              );
                            }}
                          />
                          {showError('DebitAccount') && (
                            <ErrorText>{errors.DebitAccount}</ErrorText>
                          )}
                        </div>
                      </RowItem>
                    </FormRow>
                    <TextField
                      label="Description"
                      name="Description"
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.Description}
                      variant="outlined"
                      fullWidth
                      error={showError('Description')}
                    />
                    <ButtonFlex>
                      <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        size="large"
                        disabled={!dirty || isSubmitting}
                        style={{
                          margin: '5px',
                        }}
                      >
                        {submitLabel}
                      </Button>
                      {buttons && buttons.map(button => button)}
                    </ButtonFlex>
                  </StyledForm>
                </div>
              );
            }}
          />
        );
      }}
    />
  );
};

export default DealTransactionForm;
